import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';

/**
 * Animated breathing circle.
 *
 * Props:
 *   pattern: { inhale: number, hold?: number, exhale: number }  (seconds)
 *   title?: string
 */
const PHASES = {
  inhale: { label: 'Breathe in',  scale: 1,    color: '#7a9b8e', glow: 'rgba(122,155,142,0.55)' }, // sage
  hold:   { label: 'Hold',        scale: 1,    color: '#D6AD60', glow: 'rgba(201,168,102,0.55)' }, // gold
  exhale: { label: 'Breathe out', scale: 0.45, color: '#1e3a5f', glow: 'rgba(30,58,95,0.45)'   }, // deep blue
  rest:   { label: 'Begin',       scale: 0.45, color: '#a8bfae', glow: 'rgba(168,191,174,0.35)' }, // soft sage
};

const BreathingCircle = ({ pattern, title }) => {
  const { inhale, hold = 0, exhale } = pattern;
  const [phase, setPhase] = useState('rest');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [cycles, setCycles] = useState(0);
  const [running, setRunning] = useState(false);
  const tickRef = useRef(null);
  const phaseStartRef = useRef(0);

  const phaseDuration = useCallback(
    (p) => (p === 'inhale' ? inhale : p === 'hold' ? hold : p === 'exhale' ? exhale : 0),
    [inhale, hold, exhale]
  );

  const nextPhase = useCallback(
    (p) => {
      if (p === 'rest') return 'inhale';
      if (p === 'inhale') return hold > 0 ? 'hold' : 'exhale';
      if (p === 'hold') return 'exhale';
      // exhale -> next cycle
      return 'inhale';
    },
    [hold]
  );

  // Main tick: 100ms resolution for smooth countdown
  useEffect(() => {
    if (!running) return;
    if (tickRef.current) clearInterval(tickRef.current);

    tickRef.current = setInterval(() => {
      const now = performance.now();
      const dur = phaseDuration(phase) * 1000;
      const elapsed = now - phaseStartRef.current;
      const remaining = Math.max(0, dur - elapsed);
      setSecondsLeft(Math.ceil(remaining / 1000));

      if (remaining <= 0) {
        const np = nextPhase(phase);
        if (phase === 'exhale') setCycles((c) => c + 1);
        phaseStartRef.current = performance.now();
        setPhase(np);
        setSecondsLeft(phaseDuration(np));
      }
    }, 100);

    return () => clearInterval(tickRef.current);
  }, [running, phase, phaseDuration, nextPhase]);

  const start = () => {
    if (phase === 'rest') {
      setPhase('inhale');
      setSecondsLeft(inhale);
      setCycles(0);
    }
    phaseStartRef.current = performance.now() - ((phaseDuration(phase) - secondsLeft) * 1000 || 0);
    setRunning(true);
  };

  const stop = () => {
    setRunning(false);
    if (tickRef.current) clearInterval(tickRef.current);
  };

  const reset = () => {
    stop();
    setPhase('rest');
    setSecondsLeft(0);
    setCycles(0);
  };

  const meta = PHASES[phase];
  const targetScale = meta.scale;
  // Animation duration = current phase's seconds (CSS transition)
  const transition = `transform ${phaseDuration(phase)}s cubic-bezier(0.45, 0, 0.55, 1), background-color 0.8s ease`;

  return (
    <div
      className="rbr-card p-8 flex flex-col items-center relative overflow-hidden"
      data-testid="breathing-circle"
    >
      {/* Ambient sage-gold glow drifting behind the orb */}
      <div
        className="absolute -top-16 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full pointer-events-none opacity-70"
        style={{
          background: `radial-gradient(circle, ${meta.glow} 0%, transparent 65%)`,
          transition: 'background 1.2s ease',
        }}
        aria-hidden
      />

      {title && (
        <h3 className="rbr-heading text-xl mb-1 text-center relative z-10">
          {title}
        </h3>
      )}
      <p className="rbr-eyebrow mb-8 relative z-10">
        {inhale}s in {hold > 0 ? `· ${hold}s hold ` : ''}· {exhale}s out
      </p>

      {/* Circle */}
      <div className="relative w-[360px] h-[360px] flex items-center justify-center mb-8 z-10">
        {/* Concentric guide rings for depth */}
        <div className="absolute inset-0 rounded-full border border-rbr-gold-soft/40" />
        <div className="absolute inset-6 rounded-full border border-rbr-gold-soft/25" />
        <div className="absolute inset-12 rounded-full border border-rbr-gold-soft/15" />

        {/* Soft outer halo tied to phase */}
        <div
          className="absolute inset-8 rounded-full pointer-events-none"
          style={{
            boxShadow: `0 0 80px 20px ${meta.glow}`,
            transition: 'box-shadow 1.2s ease',
          }}
          aria-hidden
        />

        {/* The breathing orb */}
        <div
          className="rounded-full flex items-center justify-center relative"
          style={{
            width: '280px',
            height: '280px',
            transform: `scale(${targetScale})`,
            transition: running ? transition : 'transform 0.5s ease, background 0.5s ease',
            background: `radial-gradient(circle at 32% 30%, ${meta.color}f2 0%, ${meta.color} 55%, ${meta.color}cc 100%)`,
            boxShadow: `0 20px 60px -12px ${meta.glow}, inset 0 -8px 24px rgba(0,0,0,0.10), inset 0 8px 24px rgba(255,255,255,0.18)`,
          }}
          data-testid="breathing-orb"
          data-phase={phase}
        >
          {/* Inner highlight for depth */}
          <div
            className="absolute inset-0 rounded-full opacity-40 pointer-events-none"
            style={{ background: 'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.5), transparent 55%)' }}
            aria-hidden
          />

          <div className="text-center text-white select-none relative z-10">
            <p
              className="text-[11px] font-semibold uppercase tracking-[0.32em] opacity-95"
              data-testid="breathing-phase"
            >
              {meta.label}
            </p>
            {running && (
              <p
                className="text-5xl font-semibold mt-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="breathing-countdown"
              >
                {secondsLeft}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 relative z-10">
        {!running ? (
          <button
            onClick={start}
            className="rbr-btn-gold px-7 py-3"
            data-testid="breathing-start-btn"
          >
            <Play className="w-4 h-4" />
            {phase === 'rest' ? 'Begin' : 'Resume'}
          </button>
        ) : (
          <button
            onClick={stop}
            className="rbr-btn-ghost px-7 py-3"
            data-testid="breathing-pause-btn"
          >
            <Pause className="w-4 h-4" />
            Pause
          </button>
        )}
        {(running || phase !== 'rest') && (
          <button
            onClick={reset}
            className="w-11 h-11 rounded-full border border-rbr-gold-soft bg-rbr-cream-warm hover:bg-rbr-gold-soft/40 flex items-center justify-center text-rbr-gold-deep transition-colors"
            data-testid="breathing-reset-btn"
            aria-label="Reset"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Cycle counter */}
      <p className="rbr-serif-italic text-[13px] text-rbr-sage-deep mt-5 relative z-10">
        {cycles === 0 ? 'Settle in and begin when ready' : `${cycles} ${cycles === 1 ? 'cycle' : 'cycles'} complete`}
      </p>
    </div>
  );
};

export default BreathingCircle;
